import { Button } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import MDSpinner from "react-md-spinner";
import { useSelector } from "react-redux";

import { Breach, LeveeMaterialEnum } from "@nens/threedi/openapi-client";

import Duration from "../components/Duration";
import NumberInput from "../components/NumberInput";
import { useModelPotentialBreach } from "../hooks/queries";
import { checkPermission } from "../state/appState";
import {
  breachEventBelongsToPotentialBreach,
  getBreachEventThatBelongsToPotentialBreach,
  getExistingBreaches,
  isBreachEventStarted,
  submitBreachEdit,
} from "../state/existingEvents";
import {
  getSimulation,
  getSimulationStatus,
  getThreediModelNotNull,
  getTime,
} from "../state/simulation";
import ErrorMessageStyles from "../styles/ErrorMessage.module.css";
import FormStyles from "../styles/Forms.module.css";
import inputElementStyles from "../styles/InputElements.module.css";
import { Breach as PotentialBreach } from "../types/assets";
import { formattedMessageForErrorCode } from "../utils/authorizationUtils";
import { hoursToSeconds, secondsToHours } from "../utils/timeConverter";

import editStyles from "./EditPropertyInfos.module.css";
import menuStyles from "./Menu.module.css";
import { PropertyTableHeader } from "./PropertyTableHeader";
import assetInspectorStyles from "./SelectedAssetInspector.module.css";

interface Props {
  potentialBreach: PotentialBreach;
  editing: boolean;
  setEditing: (bool: boolean) => void;
}

interface InfoProps {
  potentialBreach: PotentialBreach | null;
  simulationRunningTime: number;
  breachEvent: Breach | null;
  breachEventStarted: boolean;
  cancelOpenPotentialBreach: () => void;
  disableWholeForm: boolean;
  disablesWholeFormErrorMessage: string | null;
}

interface EditingProps {
  breached: boolean;
  breachEventStarted: boolean;
  durationHours: number | string;
  maximumBreachDepth: number | string;
  leveeMaterial: string;
  initialWidth: number | string;
  dischargeCoefficient: string;
  setDischargeCoefficient: (value: string) => void;
  setDurationHours: (value: string) => void;
  setMaximumBreachDepth: (value: string) => void;
  setLeveeMaterial: (value: string) => void;
  setInitialWidth: (value: string) => void;
  openPotentialBreach: () => void;
  cancelOpenPotentialBreach: () => void;
  close: () => void;
  disableWholeForm: boolean;
  disablesWholeFormErrorMessage: string | null;
}

interface BreachStatusProps {
  breached: boolean;
  breachEventStarted: boolean;
}

export const BreachStatus: React.FC<BreachStatusProps> = ({ breached, breachEventStarted }) => {
  return (
    <div className={`${assetInspectorStyles.DetailListItem} ${assetInspectorStyles.KeyValue}`}>
      <FormattedMessage id="editBreach.breachstatus" defaultMessage="Status" />
      {breached && breachEventStarted ? (
        <span className={assetInspectorStyles.Open}>
          <FormattedMessage id="editBreach.breached" defaultMessage="Breached" />
        </span>
      ) : breached ? (
        <span className={assetInspectorStyles.Open}>
          <FormattedMessage id="editBreach.upcomingbreach" defaultMessage="Upcoming Breach" />
        </span>
      ) : (
        <span className={assetInspectorStyles.Closed}>
          <FormattedMessage id="editBreach.notbreached" defaultMessage="Not breached" />
        </span>
      )}
    </div>
  );
};

export const checkEditingAllowed = (
  breachEvent: Breach | null,
  simulationRunningTime: number,
  simulationStatusStr: string
) => {
  if (
    simulationStatusStr !== "running" &&
    simulationStatusStr !== "finished" &&
    simulationStatusStr !== "crashed" &&
    (!breachEvent || !isBreachEventStarted(breachEvent, simulationRunningTime))
  ) {
    return true;
  } else {
    return false;
  }
};

const BreachEditingMenu: React.FC<EditingProps> = (props) => {
  const {
    breached,
    breachEventStarted,
    durationHours,
    maximumBreachDepth,
    leveeMaterial,
    initialWidth,
    dischargeCoefficient,
    setDischargeCoefficient,
    setDurationHours,
    setMaximumBreachDepth,
    setLeveeMaterial,
    setInitialWidth,
    openPotentialBreach,
    close,
    disableWholeForm,
    disablesWholeFormErrorMessage,
  } = props;
  const intl = useIntl();

  const selectBoxLeveeMaterialSand = intl.formatMessage({
    id: "editBreach.leveeMaterialSand",
    defaultMessage: "Sand",
  });
  const selectBoxLeveeMaterialClay = intl.formatMessage({
    id: "editBreach.leveeMaterialClay",
    defaultMessage: "Clay",
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
      }}
      className={FormStyles.NormalForm}
    >
      <fieldset disabled={disableWholeForm}>
        <div className={assetInspectorStyles.DetailList}>
          <BreachStatus breached={breached} breachEventStarted={breachEventStarted} />
          <div
            className={`${assetInspectorStyles.DetailListItem} ${assetInspectorStyles.KeyValue}`}
          >
            <FormattedMessage id="editBreach.leveeMaterial" defaultMessage="Levee material" />
            <select
              className={`${inputElementStyles.Padding} ${inputElementStyles.Input}`}
              value={leveeMaterial + ""}
              onChange={(e) => {
                setLeveeMaterial(e.target.value);
              }}
            >
              <option value={"sand"}>{selectBoxLeveeMaterialSand}</option>
              <option value={"clay"}>{selectBoxLeveeMaterialClay}</option>
            </select>
          </div>
          <div
            className={`${assetInspectorStyles.DetailListItem} ${assetInspectorStyles.KeyValue}`}
          >
            <FormattedMessage
              id="editBreach.maximumBreachDepth"
              defaultMessage="Maximum breach depth"
            />
            <NumberInput
              value={maximumBreachDepth + ""}
              unit={
                <FormattedMessage id="editBreach.unit_maximum_breach_depth_m" defaultMessage="m" />
              }
              allowDecimals
              setValue={setMaximumBreachDepth}
              required
            />
          </div>
          <div
            className={`${assetInspectorStyles.DetailListItem} ${assetInspectorStyles.KeyValue}`}
          >
            <FormattedMessage
              id="editBreach.durationTillMax"
              defaultMessage="Duration until max depth"
            />
            <NumberInput
              value={durationHours + ""}
              unit={<FormattedMessage id="editBreach.duration_unit_hour" defaultMessage="hours" />}
              zeroIsInvalid
              allowDecimals
              setValue={setDurationHours}
              required
            />
          </div>
          <div
            className={`${assetInspectorStyles.DetailListItem} ${assetInspectorStyles.KeyValue}`}
          >
            <FormattedMessage id="editBreach.initialWidth" defaultMessage="Initial width" />
            <NumberInput
              value={initialWidth + ""}
              unit={<FormattedMessage id="editBreach.unit_initial_width_m" defaultMessage="m" />}
              allowDecimals
              setValue={setInitialWidth}
              required
            />
          </div>
          <div
            className={`${assetInspectorStyles.DetailListItem} ${assetInspectorStyles.KeyValue}`}
          >
            <FormattedMessage
              id="editBreach.discharge_coef_pos_neg"
              defaultMessage="Discharge coefficient"
            />
            <NumberInput
              value={dischargeCoefficient + ""}
              unit={""}
              zeroIsInvalid
              allowDecimals
              setValue={setDischargeCoefficient}
            />
          </div>
          <div className={editStyles.ButtonDiv}>
            <Button onClick={close} variant="secondary">
              <FormattedMessage id="oneDElementInspector.cancel" defaultMessage="Cancel" />
            </Button>
            {breached ? (
              <Button
                title={disablesWholeFormErrorMessage || undefined}
                variant="primary"
                onClick={(e) => {
                  const target = e.target as HTMLButtonElement;
                  if (target.form && target.form.checkValidity && target.form.checkValidity()) {
                    openPotentialBreach();
                  }
                }}
                onMouseOver={(e) => {
                  // this clicks the button onhover when it is invalid  so the invalid messages show on the fields
                  const target = e.target as HTMLButtonElement;
                  if (!(target.form && target.form.checkValidity && target.form.checkValidity())) {
                    target.click();
                  }
                }}
              >
                <FormattedMessage
                  id="oneDElementInspector.updateBreach"
                  defaultMessage="Update breach"
                />
              </Button>
            ) : (
              <Button
                title={disablesWholeFormErrorMessage || undefined}
                variant="primary"
                onClick={(e) => {
                  const target = e.target as HTMLButtonElement;
                  if (target.form && target.form.checkValidity && target.form.checkValidity()) {
                    openPotentialBreach();
                  }
                }}
                onMouseOver={(e) => {
                  // this clicks the button onhover when it is invalid  so the invalid messages show on the fields
                  const target = e.target as HTMLButtonElement;
                  if (!(target.form && target.form.checkValidity && target.form.checkValidity())) {
                    target.click();
                  }
                }}
              >
                <FormattedMessage
                  id="oneDElementInspector.openBreach"
                  defaultMessage="Open breach"
                />
              </Button>
            )}
          </div>
        </div>
        <div className={`${menuStyles.ListItem} `}>
          {disablesWholeFormErrorMessage ? (
            <span className={ErrorMessageStyles.ErrorMessageStyle}>
              {disablesWholeFormErrorMessage}
            </span>
          ) : null}
        </div>
      </fieldset>
    </form>
  );
};

const BreachInfoMenu: React.FC<InfoProps> = ({
  potentialBreach,
  breachEvent,
  breachEventStarted,
  cancelOpenPotentialBreach,
  disablesWholeFormErrorMessage,
  disableWholeForm,
}) => {
  return (
    <div className={assetInspectorStyles.DetailList}>
      <BreachStatus breached={!!breachEvent} breachEventStarted={breachEventStarted} />
      {/* Only show duration and initial width field if there is still active breach event */}
      {breachEvent ? (
        <>
          <div
            className={`${assetInspectorStyles.DetailListItem} ${assetInspectorStyles.KeyValue}`}
          >
            <FormattedMessage id="editBreach.leveeMaterial" defaultMessage="Levee material" />
            <span>{breachEvent && breachEvent.leveeMaterial}</span>
          </div>
          <div
            className={`${assetInspectorStyles.DetailListItem} ${assetInspectorStyles.KeyValue}`}
          >
            <FormattedMessage
              id="editBreach.maximumBreachDepth"
              defaultMessage="Maximum breach depth"
            />
            <span>{breachEvent && breachEvent.maximumBreachDepth}</span>
          </div>
          <div
            className={`${assetInspectorStyles.DetailListItem} ${assetInspectorStyles.KeyValue}`}
          >
            <FormattedMessage
              id="editBreach.durationTillMax"
              defaultMessage="Duration until max depth"
            />
            <span>
              {potentialBreach && <Duration duration={breachEvent.durationTillMaxDepth} />}
            </span>
          </div>
          <div
            className={`${assetInspectorStyles.DetailListItem} ${assetInspectorStyles.KeyValue}`}
          >
            <FormattedMessage id="editBreach.initialWidth" defaultMessage="Initial width" />
            <span>{breachEvent && breachEvent.initialWidth}</span>
          </div>
          {breachEvent &&
          (breachEvent.dischargeCoefficientNegative || breachEvent.dischargeCoefficientPositive) ? (
            <div
              className={`${assetInspectorStyles.DetailListItem} ${assetInspectorStyles.KeyValue}`}
            >
              <FormattedMessage
                id="editBreach.discharge_coef_pos_neg"
                defaultMessage="Discharge coefficient"
              />
              <span>
                {breachEvent &&
                  (breachEvent.dischargeCoefficientNegative ||
                    breachEvent.dischargeCoefficientPositive)}
              </span>
            </div>
          ) : null}
        </>
      ) : null}
      {breachEvent && !breachEventStarted ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
          className={FormStyles.NormalForm}
        >
          <fieldset disabled={disableWholeForm}>
            <Button
              title={disablesWholeFormErrorMessage || undefined}
              variant="secondary"
              onClick={(e) => {
                const target = e.target as HTMLButtonElement;
                if (target.form && target.form.checkValidity && target.form.checkValidity()) {
                  cancelOpenPotentialBreach();
                }
              }}
              onMouseOver={(e) => {
                // this clicks the button onhover when it is invalid  so the invalid messages show on the fields
                const target = e.target as HTMLButtonElement;
                if (!(target.form && target.form.checkValidity && target.form.checkValidity())) {
                  target.click();
                }
              }}
            >
              <FormattedMessage
                id="oneDElementInspector.closeBreach"
                defaultMessage="Cancel upcoming breach"
              />
            </Button>
          </fieldset>
        </form>
      ) : null}
    </div>
  );
};

const parseLeveeMaterial = (s: string | number): LeveeMaterialEnum => {
  const s_clean = (s + "").toLowerCase();
  if (["1", "sand"].includes(s_clean)) {
    return LeveeMaterialEnum.SAND;
  } else if (["2", "clay"].includes(s_clean)) {
    return LeveeMaterialEnum.CLAY;
  } else {
    return LeveeMaterialEnum.SAND; // TODO: return null / undefined?
  }
};

const EditBreach: React.FC<Props> = ({ potentialBreach, editing, setEditing }) => {
  const simulation = useSelector(getSimulation);
  const simulationStatus = useSelector(getSimulationStatus);
  const simulationRunningTime = useSelector(getTime);
  const existingBreaches = useSelector(getExistingBreaches);
  const threediModel = useSelector(getThreediModelNotNull);

  const simulationId = simulation.id!;
  const threediModelId = threediModel.id!;

  const [durationHours, setDurationHours] = useState<number | string>(0.1); // default duration till max depth = 0.1 hour = 6 minutes
  const [maximumBreachDepth, setMaximumBreachDepth] = useState<number | string>(
    potentialBreach.properties.levbr || 1.0
  );
  const [leveeMaterial, setLeveeMaterial] = useState(
    (parseLeveeMaterial(potentialBreach.properties.levmat + "") || "sand") + ""
  );
  const [initialWidth, setInitialWidth] = useState<number | string>(10); // default initial width = 10m
  const [dischargeCoefficient, setDischargeCoefficient] = useState(""); // "" means no discharge coefficient

  const [submitting, setSubmitting] = useState(false);

  // Get the existed breach event for the selected breach
  const modelPotentialBreach = useModelPotentialBreach(potentialBreach, threediModelId);
  const breachEvent = getBreachEventThatBelongsToPotentialBreach(
    modelPotentialBreach,
    existingBreaches
  );
  const breachEventStarted = isBreachEventStarted(breachEvent, simulationRunningTime);

  const intl = useIntl();
  const breachEditNotAllowedPlaceholder = intl.formatMessage({
    id: "editBreach.editNotAllowed",
    defaultMessage: "Potential breach has been breached",
  });

  const permission = useSelector(checkPermission)("editBreach");
  const disableWholeForm = permission.allowed === false;
  const disablesWholeFormErrorMessage =
    permission.allowed === false ? formattedMessageForErrorCode(permission.errorCode, intl) : null;

  const openPotentialBreach = async () => {
    setSubmitting(true);
    const durationTillMaxDepth = hoursToSeconds(+durationHours); // convert hours to seconds

    await submitBreachEdit(
      modelPotentialBreach,
      threediModelId,
      simulationId,
      simulationRunningTime,
      true, // breached
      +maximumBreachDepth,
      parseLeveeMaterial(leveeMaterial),
      Math.round(durationTillMaxDepth),
      +initialWidth,
      parseFloat(dischargeCoefficient),
      breachEvent
    );

    setSubmitting(false);
    setEditing(false);
  };
  const cancelOpenPotentialBreach = async () => {
    setSubmitting(true);
    const durationTillMaxDepth = hoursToSeconds(+durationHours); // convert hours to seconds

    await submitBreachEdit(
      modelPotentialBreach,
      threediModelId,
      simulationId,
      simulationRunningTime,
      false, // breached
      +maximumBreachDepth,
      parseLeveeMaterial(leveeMaterial),
      Math.round(durationTillMaxDepth),
      +initialWidth,
      parseFloat(dischargeCoefficient),
      breachEvent
    );

    setSubmitting(false);
    setEditing(false);
  };

  useEffect(() => {
    if (
      potentialBreach &&
      breachEvent &&
      breachEventBelongsToPotentialBreach(modelPotentialBreach, breachEvent)
    ) {
      setMaximumBreachDepth(breachEvent.maximumBreachDepth || 1.0);
      setLeveeMaterial(breachEvent.leveeMaterial || "sand");
      setDurationHours(secondsToHours(breachEvent.durationTillMaxDepth)); // convert duration input from seconds to hours
      setInitialWidth(breachEvent.initialWidth);
      const tmpDischargeCoefficient = breachEvent.dischargeCoefficientPositive
        ? breachEvent.dischargeCoefficientPositive + ""
        : breachEvent.dischargeCoefficientNegative
        ? breachEvent.dischargeCoefficientNegative + ""
        : "";
      setDischargeCoefficient(tmpDischargeCoefficient);
    }
    if (breachEventStarted) {
      setEditing(false);
    }
  }, [breachEvent, potentialBreach, editing, setEditing, breachEventStarted]);

  return (
    <div className={menuStyles.List}>
      <PropertyTableHeader
        button={
          editing ? (
            <Button onClick={() => setEditing(false)} variant="secondary">
              <FormattedMessage id="oneDElementInspector.cancel" defaultMessage="Cancel" />
            </Button>
          ) : (
            <Button
              onClick={() => setEditing(true)}
              isDisabled={
                editing ||
                !checkEditingAllowed(breachEvent, simulationRunningTime, simulationStatus.status)
              }
              title={
                !checkEditingAllowed(breachEvent, simulationRunningTime, simulationStatus.status)
                  ? breachEditNotAllowedPlaceholder
                  : ""
              }
              variant="primary"
            >
              <FormattedMessage id="oneDElementInspector.edit" defaultMessage="Edit" />
            </Button>
          )
        }
      >
        <FormattedMessage id="oneDElementInspector.settingsHeader" defaultMessage="Settings" />
      </PropertyTableHeader>
      {submitting ? (
        <MDSpinner
          className={editStyles.SpinnerStyle}
          singleColor={getComputedStyle(document.documentElement).getPropertyValue(
            "--gui-action-element-hover-color"
          )}
        />
      ) : editing ? (
        <BreachEditingMenu
          breached={!!breachEvent}
          breachEventStarted={breachEventStarted}
          maximumBreachDepth={maximumBreachDepth}
          leveeMaterial={leveeMaterial}
          durationHours={durationHours}
          initialWidth={initialWidth}
          dischargeCoefficient={dischargeCoefficient}
          setDischargeCoefficient={(coef: string) => setDischargeCoefficient(coef)}
          setDurationHours={(durationHours: string) => setDurationHours(durationHours)}
          setMaximumBreachDepth={(x: string) => setMaximumBreachDepth(x)}
          setLeveeMaterial={(leveeMaterial: string) => setLeveeMaterial(leveeMaterial)}
          setInitialWidth={(initialWidth: string) => setInitialWidth(initialWidth)}
          openPotentialBreach={openPotentialBreach}
          cancelOpenPotentialBreach={cancelOpenPotentialBreach}
          close={() => setEditing(false)}
          disableWholeForm={disableWholeForm}
          disablesWholeFormErrorMessage={disablesWholeFormErrorMessage}
        />
      ) : (
        <BreachInfoMenu
          breachEvent={breachEvent}
          breachEventStarted={breachEventStarted}
          potentialBreach={potentialBreach}
          simulationRunningTime={simulationRunningTime}
          cancelOpenPotentialBreach={cancelOpenPotentialBreach}
          disableWholeForm={disableWholeForm}
          disablesWholeFormErrorMessage={disablesWholeFormErrorMessage}
        />
      )}
    </div>
  );
};

export default EditBreach;
